import React, { useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--color-Line);
    background: var(--color-Base1);
`;

const HeaderInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const HeaderText = styled.div`
    min-width: 0;
`;

const Title = styled.div`
    font-size: 13px;
    font-weight: 800;
    color: var(--color-NormalBlack);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Subtitle = styled.div`
    font-size: 11px;
    color: var(--color-Grey1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RailBody = styled.div`
    flex: 1;
    min-height: 0;
    padding: 10px;
    overflow: auto;
    background: var(--color-White);
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
    border: 1px ${({ $focused }) => ($focused ? "solid var(--color-Key)" : "dashed var(--color-Line)")};
    border-radius: 12px;
    background: ${({ $focused }) => ($focused ? "var(--color-ButtonHover4)" : "var(--color-White)")};
    color: var(--color-SubBlack);
`;

const MetaList = styled.div`
    display: grid;
    grid-template-columns: minmax(88px, 110px) minmax(0, 1fr);
    gap: 8px 12px;
    font-size: 13px;
`;

const MetaKey = styled.div`
    font-weight: 700;
    color: var(--color-NormalBlack);
`;

const MetaValue = styled.div`
    min-width: 0;
    color: var(--color-SubBlack);
    word-break: break-word;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid ${(props) => (props.$active ? "var(--color-Key)" : "var(--color-Line)")};
    border-radius: 8px;
    background: ${(props) =>
        props.$primary ? "var(--color-Key)" : props.$active ? "var(--color-ButtonHover4)" : "var(--color-White)"};
    color: ${(props) =>
        props.$primary ? "var(--color-White)" : props.$active ? "var(--color-Key)" : "var(--color-NormalBlack)"};
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => (props.$primary ? "var(--color-DarkKey)" : "var(--color-ButtonHover4)")};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

function ManuscriptHistoryPanel({
    handleRestoreRevision,
    highlightedRevisionId,
    historyItems,
    restoreMutation,
    saveLabel,
}) {
    const revisionRefs = useRef(new Map());
    const [focusedRevisionId, setFocusedRevisionId] = useState(highlightedRevisionId);
    const visibleHistoryItems = useMemo(() => {
        const latest = historyItems.slice(0, 10);
        const highlighted = historyItems.find((revision) => String(revision.id) === String(highlightedRevisionId));
        if (highlighted && !latest.some((revision) => revision.id === highlighted.id)) {
            return [...latest, highlighted];
        }
        return latest;
    }, [highlightedRevisionId, historyItems]);

    useEffect(() => {
        if (!highlightedRevisionId) return;
        const target = revisionRefs.current.get(String(highlightedRevisionId));
        if (!target) return;
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        const timer = setTimeout(() => setFocusedRevisionId(null), 3000);
        return () => clearTimeout(timer);
    }, [highlightedRevisionId, visibleHistoryItems]);

    return (
        <>
            <PanelHeader>
                <HeaderInfo>
                    <Icon name={"last"} size={"16px"} color={"var(--color-Key)"} />
                    <HeaderText>
                        <Title>히스토리</Title>
                        <Subtitle>이전 시점 복원</Subtitle>
                    </HeaderText>
                </HeaderInfo>
            </PanelHeader>
            <RailBody>
                <Stack>
                    <Card>
                        <div>문서 변경 리비전 목록입니다.</div>
                        <MetaList>
                            <MetaKey>정책</MetaKey>
                            <MetaValue>
                                같은 작성자의 5분 이내 연속 저장은 병합. 생성, 수정, 삭제, 복원 같은 주요 이벤트는 항상
                                별도 리비전으로 보관.
                            </MetaValue>
                            <MetaKey>상태</MetaKey>
                            <MetaValue>{saveLabel}</MetaValue>
                        </MetaList>
                    </Card>
                    {historyItems.length ? (
                        visibleHistoryItems.map((revision) => (
                            <Card
                                key={revision.id}
                                ref={(node) => {
                                    if (node) revisionRefs.current.set(String(revision.id), node);
                                    else revisionRefs.current.delete(String(revision.id));
                                }}
                                data-revision-id={revision.id}
                                data-focused={String(revision.id) === String(focusedRevisionId)}
                                $focused={String(revision.id) === String(focusedRevisionId)}
                            >
                                <MetaList>
                                    <MetaKey>리비전</MetaKey>
                                    <MetaValue>#{revision.revision_seq}</MetaValue>
                                    <MetaKey>유형</MetaKey>
                                    <MetaValue>{revision.revision_type}</MetaValue>
                                    <MetaKey>시간</MetaKey>
                                    <MetaValue>{revision.created_at}</MetaValue>
                                </MetaList>
                                <Actions>
                                    <Button
                                        onClick={() => handleRestoreRevision(revision.id)}
                                        disabled={restoreMutation.isLoading}
                                    >
                                        이 시점으로 복원
                                    </Button>
                                </Actions>
                            </Card>
                        ))
                    ) : (
                        <MetaValue>아직 복원 가능한 리비전이 없습니다.</MetaValue>
                    )}
                </Stack>
            </RailBody>
        </>
    );
}

export default ManuscriptHistoryPanel;
